import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Chip, CircularProgress, Paper, Typography } from "@mui/material";

function Row({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2} sx={{ py: 0.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
    </Box>
  );
}
Row.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

const status = (ok, yes = "Configured", no = "Not configured") => (
  <Chip size="small" color={ok ? "success" : "default"} label={ok ? yes : no} />
);

/** Read-only view of how this Spectra deployment is configured. Secrets are never shown. */
function SystemSettingsPanel({ apiBase }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${apiBase}/system/status`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [apiBase]);

  if (error) return <Alert severity="error">Couldn't load system status: {error}</Alert>;
  if (!data) return <CircularProgress size={24} />;

  const { ai, rate_limit: rl, integrations, counts } = data;
  return (
    <Box>
      <Typography variant="h6" mb={0.5}>System Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Read-only. These values come from environment variables on the server.
      </Typography>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={1}>AI</Typography>
            <Row label="Provider" value={ai.provider} />
            <Row label="Model" value={ai.model.replace("models/", "")} />
            <Row label="API key" value={status(ai.configured)} />
            <Row label="Patient identifiers removed before each call" value={status(ai.deidentified_prompts, "Yes", "No")} />
            <Row label="Gemini calls per minute (cap)" value={ai.calls_per_minute_cap} />
            <Row label="Agenda cache" value={`${ai.agenda_cache_hours} h, or until the chart changes`} />
            <Row label="QA / social-needs cache" value={`${ai.checks_cache_hours} h, or until the chart changes`} />
          </Paper>
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" mb={1}>Integrations & health</Typography>
            <Row label="Database" value={status(data.database === "healthy", "Healthy", "Unavailable")} />
            <Row label="n8n webhook" value={integrations.n8n_webhook} />
            <Row label="Quiz admin passcode" value={status(integrations.quiz_admin_passcode, "Set", "Not set")} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={1}>Rate limit (per visitor)</Typography>
            <Row label="Requests" value={`${rl.requests} per ${rl.window_seconds} s`} />
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {rl.routes.map((r) => <Chip key={r} size="small" variant="outlined" label={r} />)}
            </Box>
          </Paper>
        </Box>
      </Box>
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            {counts.patients} patients · {counts.organizations} organizations · {counts.users} users
          </Typography>
    </Box>
  );
}

SystemSettingsPanel.propTypes = { apiBase: PropTypes.string.isRequired };

export default SystemSettingsPanel;
